import { useState, useEffect } from "react";
import Logo from "../img/Logo_signcast_big-1080x322.png";
import { Link, useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore";

const VerifyReset = () => {
  const [resetCode, setResetCode] = useState("");
  const [countdown, setCountdown] = useState(600); // 10 minutes in seconds
  
  const { 
    verifyResetCode, 
    resetEmail, 
    isLoading, 
    error, 
    clearError,
    clearResetData
  } = useAuthStore();
  
  const navigate = useNavigate();

  // Redirect if no email in state
  useEffect(() => {
    if (!resetEmail) {
      navigate('/forgot-password');
    }
  }, [resetEmail, navigate]);

  // Countdown timer
  useEffect(() => {
    if (!resetEmail) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [resetEmail]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    clearError();
    
    const success = await verifyResetCode(resetCode);
    
    if (success) {
      navigate('/reset-password');
    }
  };

  const handleCancel = () => {
    clearResetData();
    navigate('/login');
  };

  return (
    <div className="login-container bg-blue-50 w-full h-screen flex justify-center items-center">
      <div className="bg-white w-[420px] h-[500px] rounded-lg shadow-lg">
        <div className="flex flex-row justify-center pt-10">
          <img className="w-52 h-auto" src={Logo} alt="Logo" />
        </div>
        
        <div className="text-center mt-4 mb-2">
          <h2 className="text-xl font-semibold">Verify Reset Code</h2>
          <p className="text-gray-600 text-sm px-10 py-2">
            We've sent a verification code to <strong>{resetEmail}</strong>
          </p>
          <p className="text-gray-500 text-xs">
            Code expires in <span className="font-medium">{formatTime(countdown)}</span>
          </p>
        </div>
        
        <form onSubmit={handleVerify}>
          {error && (
            <p className="text-red-500 text-[12px] text-center mt-2">{error}</p>
          )}
          
          <div className="flex flex-col items-center mt-4 px-10">
            <p className="text-black text-[14px] self-start pb-2">
              Enter the 6-digit code
            </p>
            <input
              className="text-[20px] pl-4 py-2 w-full h-[45px] rounded-lg border-2 border-gray-300 tracking-wider text-center font-medium"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value.replace(/[^0-9]/g, ''))}
              required
            />
          </div>
          
          <div className="flex flex-col px-10 mt-6">
            <button
              type="submit"
              disabled={isLoading || resetCode.length !== 6}
              className="h-[41px] w-full bg-blue-500 text-white text-[14px] font-bold rounded-lg hover:bg-blue-600 transition-colors"
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>
            
            <button
              type="button"
              onClick={handleCancel}
              className="h-[41px] w-full mt-3 border border-gray-300 text-gray-600 text-[14px] font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
          
          <div className="text-center mt-6">
            <p className="text-gray-500 text-[12px]">
              Didn't receive the code?{" "}
              <Link 
                to="/forgot-password"
                style={{ textDecoration: 'none' }}
              >
                Request again
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyReset; 